import React, { useEffect, useState } from "react";

// Ordrer og sending for nettbutikken - samme mønster som verkstedets Sendinger
// (src/components/workshop/WorkshopSendingerTab.jsx). Betalingen er reservert i
// kassen og trekkes her: "Marker som sendt" for sending, "Klar for henting" for
// henting. Når beløpet er trukket, krysser den ansatte av for at salget er slått
// i kassa i butikken, og krediterer den der ved retur ("Mangler i kassa" viser
// begge deler). Backend: src/routes/nettbutikkBehandling.js.

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const BUTIKK_LABEL = { royken: "Sport 1 Røyken", slemmestad: "Sport 1 Slemmestad" };
const BETALINGSMETODE_LABEL = { vipps: "Vipps", klarna: "Klarna" };
const BETALING_LABEL = {
  reservert: "Reservert",
  betalt: "Trukket",
  refundert: "Refundert",
  kansellert: "Frigjort",
};
const BEHANDLING_LABEL = {
  venter: "Venter",
  behandles: "Under behandling",
  sendt: "Sendt",
  klar: "Klar for henting",
  kansellert: "Kansellert",
};
const BEHANDLING_BADGE = {
  venter: "bg-amber-100 text-amber-800",
  behandles: "bg-blue-100 text-blue-800",
  sendt: "bg-green-100 text-green-800",
  klar: "bg-green-100 text-green-800",
  kansellert: "bg-gray-200 text-gray-700",
};

function fmtKr(n) {
  return `${Number(n || 0).toLocaleString("nb-NO", { maximumFractionDigits: 2 })} kr`;
}

function fmtDato(d) {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";
}

async function postJson(url, body, method = "POST") {
  const r = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json?.message || "Noe gikk galt");
  return json;
}

/** Dialog for de tre handlingene. type: "sendt" | "klar" | "kanseller". */
function HandlingModal({ handling, onClose, onDone }) {
  const { type, rad } = handling || {};
  const [sporingsnummer, setSporingsnummer] = useState("");
  const [arsak, setArsak] = useState("");
  const [behandletAv, setBehandletAv] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setSporingsnummer("");
    setArsak("Vi kan dessverre ikke levere varen(e) du bestilte.");
    setBehandletAv("");
    setError("");
  }, [rad?.id, type]);

  if (!rad) return null;

  const tekster = {
    behandling: {
      tittel: "Sett under behandling",
      knapp: "Sett under behandling og send e-post",
      info: `Kunden får e-post om at vi behandler ordren. Betalingen trekkes ikke ennå; det skjer når ordren er ${
        rad.levering.type === "sending" ? "sendt" : "klar for henting"
      }.`,
      sti: "under-behandling",
    },
    sendt: {
      tittel: "Marker som sendt",
      knapp: "Trekk betaling og marker som sendt",
      info: `Betalingen på ${fmtKr(rad.totalKr)} trekkes nå, og kunden får e-post om at ordren er sendt (med sporingslenke hvis du fyller inn sendingsnummer).`,
      sti: "marker-sendt",
    },
    klar: {
      tittel: "Klar for henting",
      knapp: "Trekk betaling og send SMS",
      info: `Betalingen på ${fmtKr(rad.totalKr)} trekkes nå, og kunden får SMS om at ordren kan hentes i ${BUTIKK_LABEL[rad.butikk]}.`,
      sti: "klar-for-henting",
    },
    kanseller: {
      tittel: "Kanseller ordre",
      knapp: "Kanseller ordren",
      info: "Reservasjonen frigjøres (kunden blir ikke belastet), varene legges tilbake på lager, og kunden får e-post med meldingen under. Kan ikke angres.",
      sti: "kanseller",
    },
  }[type];

  async function bekreft() {
    setBusy(true);
    setError("");
    try {
      const body =
        type === "sendt" ? { sporingsnummer, behandletAv } : type === "kanseller" ? { arsak, behandletAv } : { behandletAv };
      const json = await postJson(`${API_BASE_URL}/nettbutikk-ordre/${encodeURIComponent(rad.referanse)}/${tekster.sti}`, body);
      onDone(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="handling-tittel"
        className="w-full max-w-md rounded-lg bg-white shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="handling-tittel" className="text-lg font-semibold mb-1">{tekster.tittel}</h2>
        <div className="text-sm text-gray-500 mb-4">Ordre {rad.referanse} — {rad.levering.mottakerNavn}</div>

        <div className="space-y-2 text-sm">
          {type === "sendt" && (
            <div className="p-3 bg-gray-50 rounded border">
              <div>{rad.levering.mottakerNavn}</div>
              <div>{rad.levering.gate}</div>
              <div>{rad.levering.postnr} {rad.levering.poststed}</div>
            </div>
          )}
          <p className={type === "kanseller" ? "text-red-700" : "text-gray-700"}>{tekster.info}</p>

          {type === "sendt" && (
            <>
              <label htmlFor="sporingsnummer" className="block text-xs text-gray-600 mt-3">Sendingsnummer (valgfritt)</label>
              <input
                id="sporingsnummer"
                type="text"
                value={sporingsnummer}
                onChange={(e) => setSporingsnummer(e.target.value)}
                className="w-full border rounded px-2 py-1.5"
              />
            </>
          )}

          {type === "kanseller" && (
            <>
              <label htmlFor="kansellering-arsak" className="block text-xs text-gray-600 mt-3">Melding til kunden</label>
              <textarea
                id="kansellering-arsak"
                rows={3}
                maxLength={500}
                value={arsak}
                onChange={(e) => setArsak(e.target.value)}
                className="w-full border rounded px-2 py-1.5"
              />
            </>
          )}

          <label htmlFor="behandlet-av" className="block text-xs text-gray-600 mt-2">Ditt navn (valgfritt)</label>
          <input
            id="behandlet-av"
            type="text"
            value={behandletAv}
            onChange={(e) => setBehandletAv(e.target.value)}
            className="w-full border rounded px-2 py-1.5"
          />
        </div>

        {error && <p className="text-sm text-red-600 mt-3" role="alert">{error}</p>}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 rounded border text-sm">Avbryt</button>
          <button
            onClick={bekreft}
            disabled={busy}
            className={`px-3 py-1.5 rounded text-white text-sm disabled:opacity-50 ${type === "kanseller" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {busy ? "Arbeider …" : tekster.knapp}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Manuelle avkrysninger for kassasystemet i butikken: «Slått i kassa» når
 * beløpet er trukket, og «Kreditert i kassa» ved retur eller refusjon.
 */
function Kassa({ rad, onSaved }) {
  const [busy, setBusy] = useState(false);

  if (!["betalt", "refundert"].includes(rad.betalingsstatus)) return <span className="text-xs text-gray-400">—</span>;

  async function lagre(sti, body) {
    setBusy(true);
    try {
      const json = await postJson(`${API_BASE_URL}/nettbutikk-ordre/${encodeURIComponent(rad.referanse)}/${sti}`, body, "PATCH");
      onSaved(json.rad);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  }

  const o = rad.oppfyllelse || {};
  if (!o.kassaRegistrertAt) {
    return (
      <button
        onClick={() => lagre("kassa", { registrert: true })}
        disabled={busy}
        className="px-2 py-1 rounded bg-amber-100 text-amber-900 border border-amber-300 text-xs hover:bg-amber-200 disabled:opacity-50"
      >
        Marker slått i kassa
      </button>
    );
  }

  return (
    <div className="text-xs space-y-1">
      <div>
        <div className="text-green-700 font-medium">Slått i kassa</div>
        <div className="text-gray-500">{fmtDato(o.kassaRegistrertAt)}</div>
        {!o.kassaKreditertAt && (
          <button onClick={() => lagre("kassa", { registrert: false })} disabled={busy} className="text-blue-600 hover:underline">
            Angre
          </button>
        )}
      </div>
      {o.kassaKreditertAt ? (
        <div>
          <div className="text-gray-700 font-medium">Kreditert i kassa</div>
          <div className="text-gray-500">{fmtDato(o.kassaKreditertAt)}</div>
          <button onClick={() => lagre("kassa-kreditert", { kreditert: false })} disabled={busy} className="text-blue-600 hover:underline">
            Angre
          </button>
        </div>
      ) : rad.manglerKreditering ? (
        <button
          onClick={() => lagre("kassa-kreditert", { kreditert: true })}
          disabled={busy}
          className="px-2 py-1 rounded bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 disabled:opacity-50"
        >
          Marker kreditert i kassa
        </button>
      ) : (
        <button
          onClick={() => {
            if (window.confirm(`Er ordre ${rad.referanse} kreditert i kassa?`)) lagre("kassa-kreditert", { kreditert: true });
          }}
          disabled={busy}
          className="text-gray-500 hover:underline"
        >
          Kreditert i kassa …
        </button>
      )}
    </div>
  );
}

function Sporingsnummer({ rad, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(rad.oppfyllelse?.sporingsnummer || "");
  const [busy, setBusy] = useState(false);

  async function lagre() {
    setBusy(true);
    try {
      const json = await postJson(
        `${API_BASE_URL}/nettbutikk-ordre/${encodeURIComponent(rad.referanse)}/sporingsnummer`,
        { sporingsnummer: value },
        "PATCH"
      );
      onSaved(json.rad);
      setEditing(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} className="text-blue-600 hover:underline text-xs">
        {rad.oppfyllelse?.sporingsnummer || "Legg til"}
      </button>
    );
  }
  return (
    <div className="flex gap-1">
      <input
        aria-label="Sendingsnummer"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="border rounded px-1 py-0.5 text-xs w-28"
      />
      <button onClick={lagre} disabled={busy} className="text-xs text-blue-600">Lagre</button>
    </div>
  );
}

export default function NettbutikkOrdrerTab({ butikk }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("alle");
  const [status, setStatus] = useState("venter");
  const [handling, setHandling] = useState(null);
  const [melding, setMelding] = useState("");

  useEffect(() => {
    let avbrutt = false;
    const run = async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_BASE_URL}/nettbutikk-ordre/behandling?butikk=${encodeURIComponent(butikk)}&type=${type}&status=${status}`
        );
        const data = r.ok ? await r.json() : { rows: [] };
        if (!avbrutt) setRows(data.rows || []);
      } catch (e) {
        console.error("Feil ved henting av nettbutikkordrer:", e);
        if (!avbrutt) setRows([]);
      } finally {
        if (!avbrutt) setLoading(false);
      }
    };
    run();
    return () => { avbrutt = true; };
  }, [butikk, type, status]);

  function ferdig({ rad, kundenVarslet }) {
    const etter = handling.type;
    setHandling(null);
    // Ordren har fått ny status - fjern den fra "venter"-listen, ellers oppdater raden.
    // «Under behandling» venter fortsatt, så den blir stående.
    const blirStaende = status !== "venter" || rad.behandlingsstatus === "behandles";
    setRows((prev) => (blirStaende ? prev.map((r) => (r.id === rad.id ? rad : r)) : prev.filter((r) => r.id !== rad.id)));
    const hva = {
      behandling: "satt til under behandling",
      sendt: "markert som sendt",
      klar: "markert som klar for henting",
      kanseller: "kansellert",
    }[etter];
    const kassa = rad.manglerKassa ? " Husk å slå salget i kassa (se «Mangler i kassa»)." : "";
    setMelding(`Ordre ${rad.referanse} er ${hva}.${kundenVarslet ? " Kunden er varslet." : " Kunden ble IKKE varslet - ta kontakt manuelt."}${kassa}`);
    window.dispatchEvent(new Event("nettbutikkOrdreSett"));
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <label className="text-sm text-gray-600" htmlFor="ordre-type">Levering</label>
        <select id="ordre-type" value={type} onChange={(e) => setType(e.target.value)} className="border rounded px-2 py-1.5 text-sm bg-white">
          <option value="alle">Alle</option>
          <option value="sending">Sending</option>
          <option value="henting">Henting i butikk</option>
        </select>
        <label className="text-sm text-gray-600 ml-2" htmlFor="ordre-status">Status</label>
        <select id="ordre-status" value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-2 py-1.5 text-sm bg-white">
          <option value="venter">Venter på behandling</option>
          <option value="kassa">Mangler i kassa (salg eller kreditering)</option>
          <option value="ferdig">Sendt / klar for henting</option>
          <option value="kansellert">Kansellert</option>
          <option value="alle">Alle</option>
        </select>
      </div>

      {melding && (
        <div className="rounded border border-green-300 bg-green-50 text-green-800 text-sm px-4 py-2 mb-4" role="status">
          {melding}
        </div>
      )}

      {loading ? (
        <p className="text-gray-400 text-sm">Laster …</p>
      ) : rows.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Ingen ordrer {status === "venter" ? "venter på behandling" : status === "kassa" ? "mangler kassa" : "funnet"}.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border rounded bg-white text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-left">Ordre</th>
                <th className="px-3 py-2 text-left">Kunde</th>
                <th className="px-3 py-2 text-left">Varer</th>
                <th className="px-3 py-2 text-left">Levering</th>
                <th className="px-3 py-2 text-right">Beløp</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Sendingsnr.</th>
                <th className="px-3 py-2 text-left">Kassa</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const venter = ["venter", "behandles"].includes(r.behandlingsstatus);
                const kanHandle = venter && ["reservert", "betalt"].includes(r.betalingsstatus);
                const kanKansellere = venter && r.betalingsstatus === "reservert";
                return (
                  <tr key={r.id} className="border-t align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.referanse}</div>
                      <div className="text-xs text-gray-500">{fmtDato(r.opprettet)}</div>
                      <div className="text-xs text-gray-500">{BUTIKK_LABEL[r.butikk]}</div>
                      {r.miljo !== "production" && <span className="text-xs bg-amber-100 text-amber-800 rounded px-1">Test</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div>{r.levering.mottakerNavn}</div>
                      <div className="text-xs text-gray-500">{r.levering.telefon}</div>
                      <div className="text-xs text-gray-500">{r.levering.epost}</div>
                      {r.ordrebekreftelseFeil && <div className="text-xs text-red-600">Ordrebekreftelsen ble ikke sendt</div>}
                    </td>
                    <td className="px-3 py-2">
                      {r.linjer.filter((l) => l.ean !== "FRAKT").map((l) => (
                        <div key={`${l.ean}-${l.storrelse}`} className="text-xs">
                          {l.antall} × {l.navn}{l.storrelse ? ` (${l.storrelse})` : ""}
                        </div>
                      ))}
                      {r.lageravvik.length > 0 && (
                        <div className="text-xs text-red-600 mt-1">For lite på lager da ordren kom - sjekk varene</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.levering.type === "sending" ? (
                        <>
                          <div className="font-medium">Sending</div>
                          <div>{r.levering.gate}</div>
                          <div>{r.levering.postnr} {r.levering.poststed}</div>
                        </>
                      ) : (
                        <div className="font-medium">Henting i butikk</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div>{fmtKr(r.totalKr)}</div>
                      <div className="text-xs text-gray-500">
                        {BETALINGSMETODE_LABEL[r.betalingsmetode]} · {BETALING_LABEL[r.betalingsstatus] || r.betalingsstatus}
                      </div>
                      {r.refundertBelop > 0 && <div className="text-xs text-orange-700">Refundert {fmtKr(r.refundertBelop)}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs rounded px-1.5 py-0.5 ${BEHANDLING_BADGE[r.behandlingsstatus]}`}>
                        {BEHANDLING_LABEL[r.behandlingsstatus]}
                      </span>
                      {r.oppfyllelse?.behandletAv && <div className="text-xs text-gray-500 mt-1">{r.oppfyllelse.behandletAv}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {r.levering.type === "sending" && r.behandlingsstatus === "sendt" ? (
                        <Sporingsnummer rad={r} onSaved={(rad) => setRows((prev) => prev.map((x) => (x.id === rad.id ? rad : x)))} />
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Kassa
                        rad={r}
                        onSaved={(rad) => {
                          setRows((prev) =>
                            status === "kassa" && !rad.manglerKassa && !rad.manglerKreditering
                              ? prev.filter((x) => x.id !== rad.id)
                              : prev.map((x) => (x.id === rad.id ? rad : x))
                          );
                          window.dispatchEvent(new Event("nettbutikkOrdreSett"));
                        }}
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right space-y-1">
                      {kanHandle && r.behandlingsstatus === "venter" && (
                        <button onClick={() => setHandling({ type: "behandling", rad: r })} className="block w-full px-2 py-1 rounded border border-blue-300 text-blue-700 text-xs hover:bg-blue-50">
                          Under behandling
                        </button>
                      )}
                      {kanHandle && r.levering.type === "sending" && (
                        <button onClick={() => setHandling({ type: "sendt", rad: r })} className="block w-full px-2 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700">
                          Marker som sendt
                        </button>
                      )}
                      {kanHandle && r.levering.type === "henting" && (
                        <button onClick={() => setHandling({ type: "klar", rad: r })} className="block w-full px-2 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700">
                          Klar for henting
                        </button>
                      )}
                      {kanKansellere && (
                        <button onClick={() => setHandling({ type: "kanseller", rad: r })} className="block w-full px-2 py-1 rounded border border-red-300 text-red-700 text-xs hover:bg-red-50">
                          Kanseller
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <HandlingModal handling={handling} onClose={() => setHandling(null)} onDone={ferdig} />
    </div>
  );
}
